// src/services/api.js

import { getEnvironment, debugLog, errorLog, maskSensitiveData } from '../utils/environment';
import { bearerHeaders } from './authHeader';
import staticJsonService from './staticJsonService';

/**
 * Firebase Functions API URL 생성 함수
 * 🔧 [URL 분리] 읽기 전용 API는 Cloudflare Worker, 쓰기/인증 API는 Firebase Functions 사용
 */
const getAPIUrls = (envOverride = null) => {
  // [통합] environment.js의 getEnvironment 사용
  const environment = envOverride || getEnvironment();
  // [매핑] Pi 환경을 Firebase 환경으로 매핑
  const firebaseEnv = environment === 'production' ? 'production' : 'development';

  debugLog('[API] Pi Environment:', environment);
  debugLog('[API] Firebase Environment:', firebaseEnv);

  // 🔧 [URL 분리] READ/WRITE 도메인 분리 + 지역별 최적화
  const projectId = firebaseEnv === 'production'
    ? 'painori-spotnori-mainnet'
    : 'painori-spotnori';

  // 읽기 전용 API - 환경별 Cloudflare Worker 도메인 (캐싱 최적화)
  // 읽기 전용 API - 환경별 Cloudflare Worker 도메인 (캐싱 최적화)
  // 🔧 [Dev] Use Proxy in Local Development
  const API_BASE_READ = process.env.NODE_ENV === 'development'
    ? '' // Local Proxy (setupProxy.js)
    : (environment === 'testnet'
      ? 'https://test-api.painori.com'  // 테스트넷 전용 (캐시 없음)
      : 'https://spot-api.painori.com'); // 메인넷용 (캐시 최적화)

  // 쓰기/인증 API - asia-northeast3 Firebase Functions 도메인 (서울 리전)
  const getWriteDomain = () => {
    debugLog('[API] asia-northeast3 리전 사용 (서울)');
    return `https://asia-northeast3-${projectId}.cloudfunctions.net`;
  };

  const API_BASE_WRITE = getWriteDomain();

  debugLog('[API] Using READ domain (Cloudflare Worker):', API_BASE_READ);
  debugLog('[API] Using WRITE domain (Firebase Functions):', API_BASE_WRITE);
  debugLog('[API] Environment-based API routing:', { environment, firebaseEnv, readDomain: API_BASE_READ });

  return {
    // ==================== 쓰기/인증 API (WRITE 도메인) ====================
    approvePayment: `${API_BASE_WRITE}/approvePayment`,
    completePayment: `${API_BASE_WRITE}/completePayment`,
    claimTestPiBonus: `${API_BASE_WRITE}/claimTestPiBonus`,
    saveUser: `${API_BASE_WRITE}/saveUser`,
    approveAdFreePayment: `${API_BASE_WRITE}/approveAdFreePayment`,
    completeAdFreePayment: `${API_BASE_WRITE}/completeAdFreePayment`,
    approveVipPayment: `${API_BASE_WRITE}/approveVipPayment`,
    completeVipPayment: `${API_BASE_WRITE}/completeVipPayment`,
    checkSubscriptionStatus: `${API_BASE_WRITE}/checkSubscriptionStatus`,
    resetAdFree: `${API_BASE_WRITE}/resetAdFree`,
    resetVip: `${API_BASE_WRITE}/resetVip`,
    saveDonationRequest: `${API_BASE_WRITE}/saveDonationRequest`,
    purchaseSubscriptionWithStar: `${API_BASE_WRITE}/purchaseSubscriptionWithStar`, // [New] STAR로 구독 구매
    verifyRewardedAd: `${API_BASE_WRITE}/verifyRewardedAd`, // [New] 광고 보상 검증
    saveGameResult: `${API_BASE_WRITE}/saveGameResult`,
    validateAdminPassword: `${API_BASE_WRITE}/validateAdminPassword`,
    getUsageStats: `${API_BASE_WRITE}/getUsageStats`,
    setMaintenanceMode: `${API_BASE_WRITE}/setMaintenanceMode`,
    getMaintenanceStatus: `${API_BASE_WRITE}/getMaintenanceStatus`,
    sendTestPi: `${API_BASE_WRITE}/sendTestPi`,
    getAnonymousUserStats: `${API_BASE_WRITE}/getAnonymousUserStats`,
    deleteAnonymousUsers: `${API_BASE_WRITE}/deleteAnonymousUsers`,
    approveDonationPayment: `${API_BASE_WRITE}/approveDonationPayment`,
    completeDonationPayment: `${API_BASE_WRITE}/completeDonationPayment`,
    claimAttendance: `${API_BASE_WRITE}/claimAttendance`, // ✅ 출석 체크 API
    // 🔴 [REMOVED 2026-01-29] 시즌 랭킹 API 제거
    // [추가] PP 포인트 관리 API
    updatePpPoints: `${API_BASE_WRITE}/updatePpPoints`,
    updatePpPointsBatch: `${API_BASE_WRITE}/updatePpPointsBatch`,
    // [추가] DEAL 상품 구매 API
    purchaseDeal: `${API_BASE_WRITE}/purchaseDeal`,
    // [추가] Spot 생성 및 관리 API
    createSpot: `${API_BASE_WRITE}/createSpot`,
    editSpot: `${API_BASE_WRITE}/editSpot`,
    deleteSpot: `${API_BASE_WRITE}/deleteSpot`,
    toggleLike: `${API_BASE_WRITE}/toggleLike`,
    addComment: `${API_BASE_WRITE}/addComment`,
    editComment: `${API_BASE_WRITE}/editComment`,
    deleteComment: `${API_BASE_WRITE}/deleteComment`,
    // [추가] Warm-Up용 API (콜드 스타트 방지)
    warmUp: `${API_BASE_WRITE}/updatePpPoints`,
    // [추가] 그 외 누락된 티켓 관련 API
    purchaseTicket: `${API_BASE_WRITE}/purchaseTicket`,
    consumeTicket: `${API_BASE_WRITE}/consumeTicket`,

    // ==================== 읽기 전용 API (READ 도메인) ====================
    // [캐싱 최적화] Cloudflare Worker로 에지 캐싱
    // 🔴 [REMOVED 2026-01-29] 시즌 랭킹 API 제거
    // 🔴 [REMOVED 2026-01-29] getHistoricalRankings 제거
    getRankingData: `${API_BASE_READ}/getRankingData`,
    getDonationHallOfFame: `${API_BASE_READ}/getDonationHallOfFame`,

    // ==================== [Admin] 관리자 API (WRITE 도메인) ====================
    adminGetOrders: `${API_BASE_WRITE}/adminGetOrders`,
    adminUpdateOrder: `${API_BASE_WRITE}/adminUpdateOrder`,
    adminGetInquiries: `${API_BASE_WRITE}/adminGetInquiries`,
    adminAnswerInquiry: `${API_BASE_WRITE}/adminAnswerInquiry`,
    adminGetDeals: `${API_BASE_WRITE}/adminGetDeals`,
    adminUpdateDeal: `${API_BASE_WRITE}/adminUpdateDeal`,
    adminCreateDeal: `${API_BASE_WRITE}/adminCreateDeal`,

    // [Admin] Banners
    adminGetBanners: `${API_BASE_WRITE}/adminGetBanners`,
    adminCreateBanner: `${API_BASE_WRITE}/adminCreateBanner`,
    adminUpdateBanner: `${API_BASE_WRITE}/adminUpdateBanner`,
    adminDeleteBanner: `${API_BASE_WRITE}/adminDeleteBanner`,
    adminGetCityNftSupply: `${API_BASE_WRITE}/adminGetCityNftSupply`,
    adminSaveCityNftMaxSupply: `${API_BASE_WRITE}/adminSaveCityNftMaxSupply`,

    // [Admin] Votes
    adminGetVotes: `${API_BASE_WRITE}/adminGetVotes`,
    adminCreateVote: `${API_BASE_WRITE}/adminCreateVote`,
    adminUpdateVote: `${API_BASE_WRITE}/adminUpdateVote`,
    adminDeleteVote: `${API_BASE_WRITE}/adminDeleteVote`,

    // [Admin] User Management
    adminGetUsers: `${API_BASE_WRITE}/adminGetUsers`,
    adminGetUser: `${API_BASE_WRITE}/adminGetUser`,
    adminGetUserHistory: `${API_BASE_WRITE}/adminGetUserHistory`,
    adminUpdateUser: `${API_BASE_WRITE}/adminUpdateUser`,
    adminDeleteUser: `${API_BASE_WRITE}/adminDeleteUser`,
    adminBulkUpdateUsers: `${API_BASE_WRITE}/adminBulkUpdateUsers`,
    adminBulkDeleteUsers: `${API_BASE_WRITE}/adminBulkDeleteUsers`,
    adminAdjustUserPoints: `${API_BASE_WRITE}/adminAdjustUserPoints`,
    submitInquiry: `${API_BASE_WRITE}/submitInquiry`,
    updateShippingInfo: `${API_BASE_WRITE}/updateShippingInfo`,

    // Spot Go MVP
    spotGoAction: `${API_BASE_WRITE}/spotGoAction`,
    syncLandmarkCount: `${API_BASE_WRITE}/syncLandmarkCount`,
    claimPointSpot: `${API_BASE_WRITE}/claimPointSpot`,
    openGiftBox: `${API_BASE_WRITE}/openGiftBox`,
    getWalkSettings: `${API_BASE_WRITE}/getWalkSettings`,
    claimWalkMission: `${API_BASE_WRITE}/claimWalkMission`,

    // My Land (슬롯 구매·등록·점유)
    purchaseMyLandSlot: `${API_BASE_WRITE}/purchaseMyLandSlot`,
    registerMyLand: `${API_BASE_WRITE}/registerMyLand`,
    myLandCapture: `${API_BASE_WRITE}/myLandCapture`,
    renameMyLand: `${API_BASE_WRITE}/renameMyLand`,

    // CITY (구매·장착·인벤토리, 출금 제외)
    getCityConfig: `${API_BASE_WRITE}/getCityConfig`,
    getCityMintedCounts: `${API_BASE_WRITE}/getCityMintedCounts`,
    getCityInventory: `${API_BASE_WRITE}/getCityInventory`,
    purchaseCity: `${API_BASE_WRITE}/purchaseCity`,
    purchaseCitySlot: `${API_BASE_WRITE}/purchaseCitySlot`,
    equipCity: `${API_BASE_WRITE}/equipCity`,
    unequipCity: `${API_BASE_WRITE}/unequipCity`,

    // 추천인 시스템
    getMyReferralCode: `${API_BASE_WRITE}/getMyReferralCode`,
    registerReferral: `${API_BASE_WRITE}/registerReferral`,
    getReferralStatus: `${API_BASE_WRITE}/getReferralStatus`,
    claimReferralReward: `${API_BASE_WRITE}/claimReferralReward`,

    // CITY 내부 거래소 (SPOT형 CITY만 거래 가능)
    listCityForSale: `${API_BASE_WRITE}/listCityForSale`,
    cancelListing: `${API_BASE_WRITE}/cancelListing`,
    purchaseCityListing: `${API_BASE_WRITE}/purchaseCityListing`,
    getMarketListings: `${API_BASE_WRITE}/getMarketListings`,

    // BASE (CITY 연동 도시 거점)
    getBaseStatus: `${API_BASE_WRITE}/getBaseStatus`,
    baseCapture: `${API_BASE_WRITE}/baseCapture`,
    reserveBase: `${API_BASE_WRITE}/reserveBase`,
    cancelBaseReservation: `${API_BASE_WRITE}/cancelBaseReservation`,

    // STAR 구매
    purchaseStarWithPi: `${API_BASE_WRITE}/purchaseStarWithPi`,

    // World Tour 미니게임 (2026-05-09)
    saveCityGameResult: `${API_BASE_WRITE}/saveCityGameResult`
  };
};


// getAPIUrls 함수 export 추가
export { getAPIUrls };

/**
 * [중앙화] API 호출 헬퍼 함수 - 재시도 로직 및 에러 처리 통합
 * @param {string} endpoint - API 엔드포인트 URL
 * @param {Object} data - 요청 데이터
 * @param {number} retryCount - 현재 재시도 횟수 (내부 사용)
 * @param {number} maxRetries - 최대 재시도 횟수 (기본값: 1)
 */
const apiCall = async (endpoint, data, retryCount = 0, maxRetries = 1) => { // 🔧 수정: 재시도 횟수를 3회에서 1회로 감소
  try {
    // [최적화] 민감한 정보를 마스킹하여 로그 출력
    debugLog('[API] 호출:', endpoint.split('/').pop()); // 엔드포인트명만 출력
    debugLog('[API] 데이터:', maskSensitiveData(data));

    // [추가] 재시도 중인 경우 로그
    if (retryCount > 0) {
      debugLog(`[API] 재시도 중... ${retryCount}/${maxRetries}번째`);
    }

    // 🚀 [최적화] timeout 설정 (8-10초) 및 AbortController 추가
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 timeout

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: await bearerHeaders(),
      body: JSON.stringify(data),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    const result = await response.json();

    if (!response.ok) {
      // 🎯 [Critical Fix] 에러 상세 정보 포함 (모바일 디버깅 시 화면 표시용 status 포함)
      const error = new Error(result.message || result.error || 'API 호출 실패');
      error.code = result.error;
      error.details = result.details;
      error.status = response.status;
      // 일부 엔드포인트(LAND_LIMIT 등)는 counts를 details가 아닌 본문 최상위에 둠
      if (result.currentCount != null) error.currentCount = result.currentCount;
      if (result.maxCount != null) error.maxCount = result.maxCount;
      throw error;
    }

    return result;
  } catch (error) {
    errorLog('[API] 에러:', error);

    // [추가] 네트워크 에러인지 확인
    const isNetworkError = error instanceof TypeError &&
      (error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('ERR_INTERNET_DISCONNECTED'));

    // [개선] 재시도 로직 - 최대 1회까지
    if (isNetworkError && retryCount < maxRetries) {
      // [개선] Exponential backoff - 재시도 간격을 점진적으로 증가하되 최대 3초로 제한
      const delay = Math.min(Math.pow(2, retryCount) * 500, 3000); // 0.5초, 최대 3초
      debugLog(`[API] 네트워크 에러 감지, ${delay / 1000}초 후 재시도합니다...`);

      // 대기 후 재시도
      await new Promise(resolve => setTimeout(resolve, delay));

      return apiCall(endpoint, data, retryCount + 1, maxRetries);
    }

    // [추가] 재시도 실패 또는 다른 에러의 경우
    if (retryCount >= maxRetries) {
      errorLog(`[API] ${maxRetries}회 재시도 실패`);
    }

    throw error;
  }
};

/**
 * 🎯 [Stage 3 수정] Pi 결제 승인 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 */
export const approvePayment = async (paymentId, userId, theme, price, environment, paymentType) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 결제 승인 요청:', maskSensitiveData({
    paymentId,
    userId,
    theme,
    price,
    environment,
    paymentType
  }));

  return apiCall(API_URLS.approvePayment, {
    paymentId,
    userId,
    theme,
    price,
    environment,
    paymentType // 🎯 매장 인증('verify'), 테마('theme'), 기타('generic') 구분
  });
};

/**
 * 🎯 [Stage 3 수정] Pi 결제 완료 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 */
export const completePayment = async (paymentId, txid, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 결제 완료 요청:', maskSensitiveData({
    paymentId,
    txid,
    environment
  }));

  return apiCall(API_URLS.completePayment, {
    paymentId,
    txid,
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] 사용자 정보 저장
 * environment 파라미터 추가, getEnvironment() 호출 제거
 */
export const saveUser = async (userId, username, environment, accessToken) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 사용자 저장:', maskSensitiveData({
    userId,
    username,
    environment
  }));

  return apiCall(API_URLS.saveUser, {
    userId,
    username,
    environment,
    ...(accessToken ? { accessToken } : {})
  });
};


/**
 * 🎯 [Stage 3 수정] 광고 제거 결제 승인 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @deprecated 통합 구독 시스템 사용 권장 (approveSubscriptionPayment)
 */
export const approveAdFreePayment = async (paymentId, userId, duration, price, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 광고 제거 결제 승인 요청:', maskSensitiveData({
    paymentId,
    userId,
    duration,
    price,
    environment
  }));

  return apiCall(API_URLS.approveAdFreePayment, {
    paymentId,
    userId,
    duration,
    price,
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] 광고 제거 결제 완료 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @deprecated 통합 구독 시스템 사용 권장 (completeSubscriptionPayment)
 */
export const completeAdFreePayment = async (paymentId, txid, userId, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 광고 제거 결제 완료 요청:', maskSensitiveData({
    paymentId,
    txid,
    userId,
    environment
  }));

  return apiCall(API_URLS.completeAdFreePayment, {
    paymentId,
    txid,
    userId,
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] VIP 결제 승인 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @deprecated 통합 구독 시스템 사용 권장 (approveSubscriptionPayment)
 */
export const approveVipPayment = async (paymentId, userId, price, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] VIP 결제 승인 요청:', maskSensitiveData({
    paymentId,
    userId,
    price,
    environment
  }));

  return apiCall(API_URLS.approveVipPayment, {
    paymentId,
    userId,
    price,
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] VIP 결제 완료 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @deprecated 통합 구독 시스템 사용 권장 (completeSubscriptionPayment)
 */
export const completeVipPayment = async (paymentId, txid, userId, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] VIP 결제 완료 요청:', maskSensitiveData({
    paymentId,
    txid,
    userId,
    environment
  }));

  return apiCall(API_URLS.completeVipPayment, {
    paymentId,
    txid,
    userId,
    environment
  });
};

/**
 * 후원 결제 승인 요청
 */
export const approveDonationPayment = async (paymentId, userId, type, amount, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 후원 결제 승인 요청:', maskSensitiveData({
    paymentId,
    userId,
    type,
    amount,
    environment
  }));

  return apiCall(API_URLS.approveDonationPayment, {
    paymentId,
    userId,
    type,
    price: amount,
    environment
  });
};

/**
 * 후원 결제 완료 요청
 */
export const completeDonationPayment = async (paymentId, txid, userId, amount, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 후원 결제 완료 요청:', maskSensitiveData({
    paymentId,
    txid,
    userId,
    amount,
    environment
  }));

  return apiCall(API_URLS.completeDonationPayment, {
    paymentId,
    txid,
    userId,
    price: amount,  // 🎯 [수정] Firebase Functions가 기대하는 'price' 파라미터로 변경
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] 구독 상태 확인 (1시간 캐싱 - 비용 최적화)
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * 기존 광고제거권/VIP 데이터도 통합하여 반환
 */
export const checkSubscriptionStatus = async (userId, environment) => {
  const API_URLS = getAPIUrls();
  const cacheKey = `subscription_${userId}_${environment}`;
  const cacheTime = 60 * 60 * 1000; // 1시간

  // 캐시 확인
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < cacheTime) {
      debugLog('[API] 구독 상태 캐시 사용:', maskSensitiveData({ userId, environment }));
      return data;
    }
  }

  debugLog('[API] 구독 상태 확인:', maskSensitiveData({
    userId,
    environment
  }));

  const result = await apiCall(API_URLS.checkSubscriptionStatus, {
    userId,
    environment
  });

  // 캐시 저장
  localStorage.setItem(cacheKey, JSON.stringify({
    data: result,
    timestamp: Date.now()
  }));

  return result;
};

/**
 * 🎯 [Stage 3 수정] 통합 구독 결제 승인 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * 모든 구독 타입(1d, 7d, 30d)을 하나의 API로 처리
 */
export const approveSubscriptionPayment = async (paymentId, userId, subscriptionType, price, environment) => {
  debugLog('[API] 구독 결제 승인 요청:', maskSensitiveData({
    paymentId,
    userId,
    subscriptionType,
    price,
    environment
  }));

  // TODO: 백엔드 통합 완료 시 새로운 엔드포인트 사용
  // const API_URLS = getAPIUrls();
  // return apiCall(API_URLS.approveSubscription, {
  //   paymentId,
  //   userId,
  //   subscriptionType,
  //   price,
  //   environment
  // });

  // [임시] 백엔드 통합 전까지 기존 API 사용
  if (subscriptionType === '30d') {
    return approveVipPayment(paymentId, userId, price, environment);
  } else {
    const duration = subscriptionType === '1d' ? '24h' : '7d';
    return approveAdFreePayment(paymentId, userId, duration, price, environment);
  }
};

/**
 * 🎯 [Stage 3 수정] 통합 구독 결제 완료 요청
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * 모든 구독 타입(1d, 7d, 30d)을 하나의 API로 처리
 */
export const completeSubscriptionPayment = async (paymentId, txid, userId, subscriptionType, environment) => {
  debugLog('[API] 구독 결제 완료 요청:', maskSensitiveData({
    paymentId,
    txid,
    userId,
    subscriptionType,
    environment
  }));

  // TODO: 백엔드 통합 완료 시 새로운 엔드포인트 사용
  // [임시] 백엔드 통합 전까지 기존 API 사용
  if (subscriptionType === '30d') {
    return completeVipPayment(paymentId, txid, userId, environment);
  } else {
    return completeAdFreePayment(paymentId, txid, userId, environment);
  }
};

/**
 * 🎯 [Stage 3 수정] 광고 제거 상태 초기화 (sandbox 전용)
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @deprecated 통합 구독 시스템 사용 권장 (resetSubscriptionStatus)
 */
export const resetAdFreeStatus = async (userId, devId, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 광고 제거 상태 초기화:', maskSensitiveData({
    userId,
    environment
  }));

  return apiCall(API_URLS.resetAdFree, {
    userId,
    environment,
    devId
  });
};

/**
 * 🎯 [Stage 3 수정] VIP 상태 초기화 (sandbox 전용)
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @deprecated 통합 구독 시스템 사용 권장 (resetSubscriptionStatus)
 */
export const resetVipStatus = async (userId, devId, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] VIP 상태 초기화:', maskSensitiveData({
    userId,
    environment
  }));

  return apiCall(API_URLS.resetVip, {
    userId,
    environment,
    devId
  });
};

/**
 * 🎯 [Stage 3 수정] 통합 구독 상태 초기화 (sandbox 전용)
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * 모든 구독 타입을 한번에 초기화
 */
export const resetSubscriptionStatus = async (userId, devId, environment) => {
  debugLog('[API] 구독 상태 초기화:', maskSensitiveData({
    userId,
    environment
  }));

  // TODO: 백엔드 통합 완료 시 새로운 엔드포인트 사용
  // [임시] 백엔드 통합 전까지 기존 API 모두 호출
  try {
    await Promise.all([
      resetAdFreeStatus(userId, devId, environment).catch(e => errorLog('[API] 광고제거 초기화 실패:', e)),
      resetVipStatus(userId, devId, environment).catch(e => errorLog('[API] VIP 초기화 실패:', e))
    ]);

    return { success: true };
  } catch (error) {
    throw new Error('구독 상태 초기화 실패');
  }
};

// ==================== 게임 통계 관련 API ====================

/**
 * 🎯 [Stage 3 수정] 사용자 게임 통계 업데이트
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * Firebase의 users 컬렉션에 게임 통계 저장
 */
export const updateUserStats = async (userId, stats, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 사용자 통계 업데이트:', maskSensitiveData({
    userId,
    stats,
    environment
  }));

  // saveUser API를 활용하여 통계 업데이트
  // 추후 별도 Functions 생성 시 변경 예정
  const username = sessionStorage.getItem('pi-username') || 'unknown';

  return apiCall(API_URLS.saveUser, {
    userId,
    username,
    environment,
    // 추가 필드들
    totalScore: stats.totalHonorScore,
    totalClearCount: stats.totalClearCount,
    currentRank: stats.currentRank,
    lastUpdated: new Date().toISOString()
  });
};

/**
 * [삭제] getUserStats 함수 제거 - checkSubscriptionStatus와 중복 호출 발생
 * 대신 checkSubscriptionStatus를 직접 사용하여 중복 API 호출 방지
 */

// ==================== 후원 관련 API ====================

/**
 * 🎯 [Stage 3 수정] 후원 내역 등록
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @param {Object} donationData - 후원 정보
 * @param {string} donationData.pi_username - 파이 닉네임
 * @param {number} donationData.donation_amount - 후원 금액
 * @param {string} donationData.transaction_id - 거래 ID (해시)
 * @param {string} donationData.submitted_at - 제출 시간
 * @param {boolean} donationData.is_verified - 검증 여부
 * @param {string} environment - 환경 정보
 */
export const saveDonationRequest = async (donationData, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 후원 등록 요청:', maskSensitiveData({
    ...donationData,
    environment
  }));

  return apiCall(API_URLS.saveDonationRequest, {
    ...donationData,
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] 명예의 전당 데이터 조회 (1시간 캐싱 - 비용 최적화)
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * 구글 시트에서 검증된 후원자 중 상위 10명 조회
 * @param {string} environment - 환경 정보
 * @returns {Promise<Array>} 명예의 전당 리스트
 */
export const getDonationHallOfFame = async (environment) => {
  const API_URLS = getAPIUrls();
  const cacheKey = `hall_of_fame_${environment}`;
  const cacheTime = 24 * 60 * 60 * 1000; // 24시간

  // 캐시 확인
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < cacheTime) {
      debugLog('[API] 명예의 전당 데이터 캐시 사용:', { environment });
      return data;
    }
  }

  debugLog('[API] 명예의 전당 조회:', { environment });

  const result = await apiCall(API_URLS.getDonationHallOfFame, {
    environment
  });

  // result.data가 배열 형태로 반환됨
  // [{rank: 1, pi_username: 'user1', total_amount: 1000}, ...]
  const data = result.data || [];

  // 캐시 저장
  localStorage.setItem(cacheKey, JSON.stringify({
    data: data,
    timestamp: Date.now()
  }));

  return data;
};

/**
 * 🎯 [Stage 3 수정] 게임 결과 저장 - dev_scores 대신 users 컬렉션에 직접 저장
 * environment 파라미터 추가, getEnvironment() 호출 제거
 * @param {Object} gameData - 게임 결과 데이터
 * @param {string} gameData.userId - 사용자 ID
 * @param {string} gameData.themeId - 테마 ID
 * @param {number} gameData.totalScore - 총 점수
 * @param {boolean} gameData.isPerfect - 퍼펙트 클리어 여부
 * @param {number} gameData.clearTime - 클리어 시간
 * @param {string} gameData.playMode - 플레이 모드 ('theme' | 'random')
 * @param {string} environment - 환경 정보
 */
export const saveGameResult = async (gameData, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 게임 결과 저장:', maskSensitiveData({
    ...gameData,
    environment
  }));

  return apiCall(API_URLS.saveGameResult, {
    ...gameData,
    environment
  });
};

/**
 * 🎯 [신규] 스팟 수정
 */
export const editSpot = async (spotId, updateData, piUser, environment) => {
  const API_URLS = getAPIUrls();
  debugLog('[API] 스팟 수정:', maskSensitiveData({ spotId, userId: piUser.uid }));
  return apiCall(API_URLS.editSpot, { spotId, updateData, piUser, environment });
};

/**
 * 🎯 [신규] 스팟 삭제
 */
export const deleteSpot = async (spotId, piUser, environment) => {
  const API_URLS = getAPIUrls();
  debugLog('[API] 스팟 삭제:', maskSensitiveData({ spotId, userId: piUser.uid }));
  return apiCall(API_URLS.deleteSpot, { spotId, piUser, environment });
};

/**
 * 🎯 [신규] 좋아요 토글
 */
export const toggleLike = async (spotId, piUser, environment) => {
  const API_URLS = getAPIUrls();
  // debugLog too frequent for likes? Maybe okay.
  return apiCall(API_URLS.toggleLike, { spotId, piUser, environment });
};

/**
 * 🎯 [신규] 댓글 추가
 */
export const addComment = async (spotId, text, piUser, environment) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.addComment, { spotId, text, piUser, environment });
};

// ==================== [신규 추가] 개발자 도구 관리자 API ====================

/**
 * 🎯 [Stage 3 수정] 관리자 비밀번호 검증 및 토큰 발급
 * environment 파라미터 추가
 */
export const validateAdminPassword = async (password, userId, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 관리자 비밀번호 검증 및 토큰 요청:', maskSensitiveData({
    environment
  }));

  // userId는 하위 호환성을 위해 유지하되, 서버 API 호출 시에는 제외 가능 (서버가 무시함)
  // 하지만 여기서는 기존 시그니처 유지
  return apiCall(API_URLS.validateAdminPassword, {
    password,
    // userId, // 서버에서 제거됨
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] 사용량 통계 조회
 * environment 파라미터 추가, getEnvironment() 호출 제거
 */
export const getUsageStats = async (userId, adminPassword, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 사용량 통계 조회:', maskSensitiveData({
    userId,
    environment
  }));

  return apiCall(API_URLS.getUsageStats, {
    userId,
    adminPassword,
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] 점검 모드 설정
 * environment 파라미터 추가, getEnvironment() 호출 제거
 */
export const setMaintenanceMode = async (userId, adminPassword, maintenanceMode, message, blockedFeatures, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 점검 모드 설정:', maskSensitiveData({
    userId,
    maintenanceMode,
    message,
    environment
  }));

  return apiCall(API_URLS.setMaintenanceMode, {
    userId,
    adminPassword,
    maintenanceMode,
    message,
    blockedFeatures,
    environment
  });
};

/**
 * 🎯 [Stage 3 수정] 점검 모드 상태 조회
 * environment 파라미터 추가, getEnvironment() 호출 제거
 */
export const getMaintenanceStatus = async (environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 점검 모드 상태 조회:', { environment });

  return apiCall(API_URLS.getMaintenanceStatus, {
    environment
  });
};

/**
 * 배송지 정보 업데이트
 */
export const updateShippingInfo = async (userId, shippingInfo, environment) => {
  const API_URLS = getAPIUrls();
  debugLog('[API] 배송지 정보 업데이트:', maskSensitiveData({ userId, environment }));

  return apiCall(API_URLS.updateShippingInfo, {
    userId,
    shippingInfo,
    environment
  });
};

// ==================== [하위 호환성] 기존 함수들 ====================

/**
 * @deprecated 통합 구독 시스템 사용 권장 (processPiPaymentSubscription)
 */
export const processPiPaymentAdFree = async (duration, _onSuccess, _onError) => {
  console.warn('[API] processPiPaymentAdFree is deprecated. Use processPiPaymentSubscription instead.');
  const subscriptionType = duration === '24h' ? '1d' : '7d';
  // 이 함수는 AppContext에서 처리하므로 여기서는 단순 매핑만
  return { subscriptionType };
};

/**
 * @deprecated 통합 구독 시스템 사용 권장 (processPiPaymentSubscription)
 */
export const processPiPaymentVip = async (_onSuccess, _onError) => {
  console.warn('[API] processPiPaymentVip is deprecated. Use processPiPaymentSubscription instead.');
  // 이 함수는 AppContext에서 처리하므로 여기서는 단순 매핑만
  return { subscriptionType: '30d' };
};

// ==================== [✅ 수정됨] App-to-User (A2U) Test-Pi 전송 API ====================

/**
 * Test-Pi 전송 (App-to-User)
 * 테스트넷 전용 - 개발자만 사용 가능
 * 
 * 🔴 수정사항:
 * - recipientAddress 파라미터 제거 (Pi 서버에서 자동 조회)
 * - 파라미터 순서 변경 (recipientUid가 첫 번째)
 * 
 * @param {string} recipientUid - 받는 사람 Pi 사용자 ID (UID)
 * @param {number} amount - 전송할 Test-Pi 금액
 * @param {string} userId - 현재 사용자 ID (개발자 ID여야 함)
 * @param {string} environment - 현재 환경 (testnet/sandbox만 허용)
 * @param {boolean} debug - 디버그 모드 활성화 여부
 */
export const sendTestPi = async (recipientUid, amount, userId, environment, debug = false) => {
  // 디버그 모드 활성화 시 상세 로그
  if (debug) {
    debugLog('[API] Test-Pi 전송 요청 (DEBUG MODE):', {
      recipientUid: recipientUid.substring(0, 8) + '...',
      amount,
      userId: userId.substring(0, 8) + '...',
      environment,
      debug
    });
  } else {
    debugLog('[API] Test-Pi 전송 요청:', maskSensitiveData({
      recipientUid,
      amount,
      userId,
      environment
    }));
  }

  // 테스트넷 환경 체크
  if (environment !== 'testnet' && environment !== 'sandbox') {
    throw new Error('Test-Pi sending is only available in testnet environment');
  }

  const API_URLS = getAPIUrls();

  // sendTestPi URL 확인
  if (!API_URLS.sendTestPi) {
    errorLog('[API] sendTestPi URL이 정의되지 않음');
    throw new Error('sendTestPi endpoint not configured');
  }

  return apiCall(API_URLS.sendTestPi, {
    recipientUid,
    amount,
    userId,
    environment,
    debug
  });
};

// ==================== [Admin] User Management API ====================

export const adminGetUsers = async (adminPassword, environment, options) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.adminGetUsers, { adminPassword, environment, ...options });
};

export const adminUpdateUser = async (adminPassword, environment, userId, updateData) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.adminUpdateUser, { adminPassword, environment, userId, updateData });
};

export const adminDeleteUser = async (adminPassword, environment, userId) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.adminDeleteUser, { adminPassword, environment, userId });
};

export const adminBulkUpdateUsers = async (adminPassword, environment, userIds, updateData) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.adminBulkUpdateUsers, { adminPassword, environment, userIds, updateData });
};

export const adminBulkDeleteUsers = async (adminPassword, environment, userIds) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.adminBulkDeleteUsers, { adminPassword, environment, userIds });
};

export const adminGetUser = async (adminPassword, environment, userId) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.adminGetUser, { adminPassword, environment, userId });
};

export const adminGetUserHistory = async (adminPassword, environment, userId, type) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.adminGetUserHistory, { adminPassword, environment, userId, type });
};

export const adminAdjustUserPoints = (adminPassword, environment, userId, amount, reason, targetType = 'point') => {
  return apiCall('adminAdjustUserPoints', 'POST', { adminPassword, environment, userId, amount, reason, targetType });
};

export const submitInquiryAPI = async (userId, form, environment) => {
  const API_URLS = getAPIUrls();
  return apiCall(API_URLS.submitInquiry, {
    userId,
    title: form.title,
    content: form.content,
    email: form.email,
    environment
  });
};

// Test-Pi 이벤트 관련 API 추가
export const claimTestPiBonus = async (userId, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] Test-Pi 보너스 청구:', maskSensitiveData({
    userId,
    environment
  }));

  // 실제 API 호출 (배포 완료)
  return apiCall(API_URLS.claimTestPiBonus, {
    userId,
    environment,
    eventId: 'current',
    timestamp: Date.now()
  });
};
/**
 * 랭킹 데이터 조회 (30분 캐싱 - 비용 최적화)
 * @param {string} environment - 환경 정보
 * @param {string} playMode - 플레이 모드 ('total' 또는 'random')
 */
export const getRankingData = async (environment, playMode = 'total') => {
  // 🚀 [성능 최적화] 정적 JSON 파일 사용으로 변경
  const cacheKey = `ranking_${playMode}_${environment}`;
  const cacheTime = 10 * 60 * 1000; // 10분 (성능 최적화)

  // 캐시 확인
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < cacheTime) {
      debugLog('[API] 랭킹 데이터 캐시 사용:', { environment, playMode });
      return data;
    }
  }

  debugLog('[API] 정적 랭킹 데이터 조회:', { environment, playMode });

  try {
    // 🚀 [핵심 변경] Cloudflare Pages CDN에서 정적 파일 직접 접근
    const cdnUrl = `https://spot.painori.com/static/ranking.json`;
    const response = await fetch(cdnUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const rankingData = await response.json();

    // 환경별 데이터 추출
    const envData = rankingData[environment] || rankingData.production;
    if (!envData) {
      throw new Error(`랭킹 데이터를 찾을 수 없습니다: ${environment}`);
    }

    // 🆕 [2026-01-29] 주간 랭킹 데이터만 사용 (랜덤 플레이 랭킹 제거됨)
    const result = envData.rankings;

    // 캐시 저장
    localStorage.setItem(cacheKey, JSON.stringify({
      data: result,
      timestamp: Date.now()
    }));

    debugLog('[API] 정적 랭킹 데이터 로딩 완료:', {
      environment,
      playMode,
      count: result.length,
      generatedAt: envData.generatedAt
    });

    return result;

  } catch (error) {
    debugLog('[API] 정적 랭킹 데이터 로딩 실패, 기존 API로 폴백:', error.message);

    // 🛡️ [폴백] 정적 파일 실패 시 기존 API 사용
    const API_URLS = getAPIUrls();
    const result = await apiCall(API_URLS.getRankingData, {
      environment,
      playMode
    });

    // 캐시 저장
    localStorage.setItem(cacheKey, JSON.stringify({
      data: result,
      timestamp: Date.now()
    }));

    return result;
  }
};

// 미완료 결제 취소 요청 (개별 결제건)
export const cancelPayment = async (paymentId, environment) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] 결제 취소 요청:', { paymentId, environment });

  const baseUrl = API_URLS.approvePayment.split('/approvePayment')[0];

  return apiCall(`${baseUrl}/cancelPayment`, {
    paymentId,
    environment
  });
};

/**
 * 출금은 됐는데 앱에서 완료 처리되지 않은 결제 복구 (승인 후 타임아웃 등).
 * Pi에서 txid 조회 후 complete 호출로 우리 쪽 상태 동기화. 복구 실패 시에만 cancel 시도.
 */
export const recoverPendingPayment = async (paymentId, environment) => {
  const API_URLS = getAPIUrls();
  const baseUrl = API_URLS.approvePayment.split('/approvePayment')[0];

  debugLog('[API] 결제 복구 시도:', { paymentId, environment });

  return apiCall(`${baseUrl}/recoverPendingPayment`, {
    paymentId,
    environment
  });
};

// 🔴 [REMOVED 2026-01-29] 시즌 랭킹 API 전체 제거
// - saveSeasonRanking
// - getSeasonRankingData
// - getSeasonRanking
// - getMySeasonRanking
// - getHistoricalRankings

// ==================== [신규 추가] PP 포인트 관리 API ====================

/**
 * 🎯 PP 포인트 업데이트 (V2.0 - 티켓 동기화 지원)
 * @param {string} userId - 사용자 ID
 * @param {number} amount - 변경할 PP 포인트 양 (양수: 추가, 음수: 차감)
 * @param {string} source - 변경 원인 (ad_watch, ticket_purchase, game_reward 등)
 * @param {string} environment - 환경
 * @param {string} requestId - 요청 ID (멱등성 처리용, 선택적)
 * @param {number} ticketDelta - 티켓 증가량 (선택적, Server-First Strategy)
 */
export const updatePpPoints = async (userId, amount, source, environment, requestId = null, ticketDelta = null) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] PP 포인트 업데이트:', { userId, amount, source, environment, requestId, ticketDelta });

  return apiCall(API_URLS.updatePpPoints, {
    userId,
    amount,
    source,
    environment,
    ...(requestId && { requestId }),
    ...(ticketDelta && { ticketDelta }) // 🎯 [NEW] 티켓 증가량 전달
  });
};

/**
 * 🔧 [V1.5] PP 포인트 배치 업데이트
 * @param {string} userId - 사용자 ID
 * @param {Array} requestIds - 요청 ID 배열
 * @param {number} totalAmount - 총 변경량
 * @param {string} source - 변경 원인
 * @param {string} environment - 환경
 */
export const updatePpPointsBatch = async (userId, requestIds, totalAmount, source, environment, sourceMap = null, totalTicketDelta = 0) => {
  const API_URLS = getAPIUrls();

  debugLog('[API] PP 포인트 배치 업데이트:', { userId, requestIds, totalAmount, source, environment, sourceMap, totalTicketDelta });

  return apiCall(API_URLS.updatePpPointsBatch, {
    userId,
    requestIds,
    totalAmount,
    source,
    environment,
    sourceMap, // 🎯 [Fix] 각 항목의 source를 유지하기 위한 sourceMap 추가
    totalTicketDelta // 🎯 [NEW] 티켓 변경량 추가
  });
};

// 🚀 [최적화] 정적 JSON 최적화된 읽기 전용 API 함수들
export const getDonationHallOfFameOptimized = async (params = {}) => {
  debugLog('[API] 최적화된 Hall of Fame 조회');
  return staticJsonService.fetchData('getDonationHallOfFame', params);
};

// 🔴 [REMOVED 2026-01-29] 시즌 랭킹 최적화 함수 제거

export const getRankingDataOptimized = async (params = {}) => {
  debugLog('[API] 최적화된 랭킹 데이터 조회');
  return staticJsonService.fetchData('getRankingData', params);
};

// 🚀 [최적화] 배치 데이터 가져오기
export const getBatchRankingData = async (endpoints = ['getDonationHallOfFame', 'getRankingData']) => {
  debugLog('[API] 배치 랭킹 데이터 조회:', endpoints);
  return staticJsonService.fetchBatchData(endpoints);
};

/**
 * DEAL 상품 구매 (트랜잭션)
 * @param {string} dealId - 상품 ID
 * @param {number} quantity - 수량
 * @param {string} userId - 사용자 ID
 * @param {Object} shippingInfo - 배송 정보 (선택)
 * @param {string} environment - 환경 (production, testnet)
 */
export const purchaseDeal = async (dealId, quantity, userId, shippingInfo = {}, environment = 'production') => {
  const { purchaseDeal: url } = getAPIUrls();
  return apiCall(url, { dealId, quantity, userId, shippingInfo, environment });
};

/**
 * 티켓 사용 (Cloud Function)
 * @param {Object} piUser - Pi 사용자 객체 { uid, username }
 * @param {string} reason - 사용 사유
 * @param {string} environment - 환경
 */
export const consumeTicket = async (piUser, reason, environment) => {
  const { consumeTicket: url } = getAPIUrls();
  return apiCall(url, { piUser, reason, environment });
};

// ==================== [Admin] 관리자 API Wrappers ====================

// getUsageStats is already defined above

export const adminGetOrders = async (adminPassword, environment, options = {}) => {
  const { adminGetOrders: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, ...options });
};

export const adminUpdateOrder = async (adminPassword, environment, orderId, updateData) => {
  const { adminUpdateOrder: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, orderId, updateData });
};

export const adminGetInquiries = async (adminPassword, environment, options = {}) => {
  const { adminGetInquiries: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, ...options });
};

export const adminAnswerInquiry = async (adminPassword, environment, inquiryId, answer) => {
  const { adminAnswerInquiry: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, inquiryId, answer });
};

export const adminGetDeals = async (adminPassword, environment, options = {}) => {
  const { adminGetDeals: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, ...options });
};

export const adminUpdateDeal = async (adminPassword, dealId, updateData, environment) => {
  const { adminUpdateDeal: url } = getAPIUrls();
  return apiCall(url, { adminPassword, dealId, updateData, environment });
};

export const adminCreateDeal = async (adminPassword, dealData, environment) => {
  const { adminCreateDeal: url } = getAPIUrls();
  return apiCall(url, { adminPassword, dealData, environment });
};

// ==================== [Admin] Banner API Wrappers ====================

export const adminGetBanners = async (adminPassword, environment, options = {}) => {
  const { adminGetBanners: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, ...options });
};

export const adminCreateBanner = async (adminPassword, bannerData, environment) => {
  const { adminCreateBanner: url } = getAPIUrls();
  return apiCall(url, { adminPassword, bannerData, environment });
};

export const adminUpdateBanner = async (adminPassword, bannerId, updateData, environment) => {
  const { adminUpdateBanner: url } = getAPIUrls();
  return apiCall(url, { adminPassword, bannerId, updateData, environment });
};

export const adminDeleteBanner = async (adminPassword, bannerId, environment) => {
  const { adminDeleteBanner: url } = getAPIUrls();
  return apiCall(url, { adminPassword, bannerId, environment });
};

/** CITY NFT 판매 상한·발행 수 조회 (관리자, 배너와 동일 mainnet 교차 DB) */
export const adminGetCityNftSupply = async (adminPassword, environment) => {
  const { adminGetCityNftSupply: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment });
};

export const adminSaveCityNftMaxSupply = async (adminPassword, environment, cityId, maxSupply) => {
  const { adminSaveCityNftMaxSupply: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, cityId, maxSupply });
};

// ==================== [Admin] Vote API Wrappers ====================

export const adminGetVotes = async (adminPassword, environment, options = {}) => {
  const { adminGetVotes: url } = getAPIUrls();
  return apiCall(url, { adminPassword, environment, ...options });
};

export const adminCreateVote = async (adminPassword, voteData, environment) => {
  const { adminCreateVote: url } = getAPIUrls();
  return apiCall(url, { adminPassword, voteData, environment });
};

export const adminUpdateVote = async (adminPassword, voteId, updateData, environment) => {
  const { adminUpdateVote: url } = getAPIUrls();
  return apiCall(url, { adminPassword, voteId, updateData, environment });
};

export const adminDeleteVote = async (adminPassword, voteId, environment) => {
  const { adminDeleteVote: url } = getAPIUrls();
  return apiCall(url, { adminPassword, voteId, environment });
};

// ==================== Spot Go MVP - 랜드마크 방문 API ====================

/**
 * 랜드마크 방문 또는 적립 요청
 * @param {string} action - 'capture' | 'claim'
 * @param {string} landmarkId - 랜드마크 ID (예: 'kr_seoul_15')
 * @param {Object} piUser - Pi User 인증 정보 { uid, username }
 * @param {string} environment - 환경 정보
 * @param {Object} landmarkData - 랜드마크 데이터 (선택적, 클라이언트에서 제공)
 */
// 🐛 [Bugfix 2026-07-09] 동일 요청(같은 유저+랜드마크+액션)이 짧은 시간에 중복 발생해도
// (중복 클릭, 네트워크 재시도 등) 서버에 두 번 전송되지 않도록 진행 중인 요청을 재사용
const spotGoActionInFlight = new Map();

export const spotGoAction = async (action, landmarkId, piUser, environment, landmarkData, isAdReward = false) => {
  const { spotGoAction: url } = getAPIUrls();

  const requestKey = `${piUser?.uid}:${landmarkId}:${action}`;
  const existing = spotGoActionInFlight.get(requestKey);
  if (existing) {
    debugLog('[API] Spot Go 액션 - 중복 요청 감지, 기존 요청 재사용:', requestKey);
    return existing;
  }

  debugLog('[API] Spot Go 액션:', maskSensitiveData({
    action,
    landmarkId,
    userId: piUser.uid,
    environment,
    isAdReward
  }));

  const promise = apiCall(url, {
    action,
    landmarkId,
    piUser,
    environment,
    landmarkData,
    isAdReward // 🎯 [Phase 4] 광고 보상 여부 추가
  }).finally(() => {
    spotGoActionInFlight.delete(requestKey);
  });

  spotGoActionInFlight.set(requestKey, promise);
  return promise;
};

/**
 * 🎯 [Auto-Fix] 랜드마크 개수 동기화 및 초과 점유 자동 정리
 * @param {Object} piUser - Pi User 인증 정보 { uid, username }
 * @param {string} environment - 환경 정보
 */
export const syncLandmarkCount = async (piUser, environment) => {
  const { syncLandmarkCount: url } = getAPIUrls();

  debugLog('[API] 랜드마크 개수 동기화:', maskSensitiveData({
    userId: piUser.uid,
    environment
  }));

  return apiCall(url, {
    piUser,
    environment
  });
};

/**
 * My Land 슬롯 구매 (7일권/영구권)
 * @param {string} userId
 * @param {string} environment
 * @param {number} slot - 2 | 3
 * @param {string} type - '7d' | 'permanent'
 */
export const purchaseMyLandSlot = async (userId, environment, slot, type, piUser = null) => {
  const { purchaseMyLandSlot: url } = getAPIUrls(environment);
  debugLog('[API] My Land 슬롯 구매:', { userId, environment, slot, type });
  return apiCall(url, { userId, environment, slot, type, ...(piUser ? { piUser } : {}) });
};

/**
 * My Land 위치·이름 등록
 * @param {string} userId
 * @param {string} environment
 * @param {number} slot - 1 | 2 | 3
 * @param {number} lat
 * @param {number} lng
 * @param {string} name
 */
export const registerMyLand = async (userId, environment, slot, lat, lng, name, piUser = null) => {
  const { registerMyLand: url } = getAPIUrls(environment);
  debugLog('[API] My Land 등록:', { userId, environment, slot });
  return apiCall(url, { userId, environment, slot, lat, lng, name: name || '', ...(piUser ? { piUser } : {}) });
};

/**
 * My Land 방문(점유) - 본인 슬롯만 가능
 * @param {string} userId
 * @param {string} environment
 * @param {number} slot - 1 | 2 | 3
 * @param {Object} piUser - { uid, username }
 * @param {boolean} isAdReward
 */
export const myLandCapture = async (userId, environment, slot, piUser, isAdReward = false) => {
  const { myLandCapture: url } = getAPIUrls(environment);
  debugLog('[API] My Land 점유:', { userId, environment, slot, isAdReward });
  return apiCall(url, { userId, environment, slot, piUser, isAdReward });
};

/**
 * My Land 이름 변경 (1000 STAR 서버 차감, functions/myLand.js MY_LAND_RENAME_COST와 동일)
 */
export const renameMyLand = async (userId, environment, slot, newName, piUser) => {
  const { renameMyLand: url } = getAPIUrls(environment);
  debugLog('[API] My Land 이름 변경:', { userId, environment, slot });
  return apiCall(url, { userId, environment, slot, newName, piUser });
};

/**
 * 🎯 STAR 구매 완료 요청 (Pi 결제 검증 및 STAR 지급)
 * - Pi SDK 결제 완료 단계(onReadyForServerCompletion)에서 호출됩니다.
 * - 서버에서 Pi Platform API `/complete` 호출까지 포함하여
 *   developer_completed 상태를 확정한 뒤 STAR를 지급합니다.
 *
 * @param {string} packageId - 패키지 ID (예: 'pack_1000', 'pack_5000')
 * @param {string} paymentId - Pi 결제 ID
 * @param {string} txid - 블록체인 트랜잭션 ID
 * @param {string} userId - 사용자 ID
 * @param {string} environment - 환경 정보
 */
export const purchaseStarWithPi = async (packageId, paymentId, txid, userId, environment) => {
  const { purchaseStarWithPi: url } = getAPIUrls();

  debugLog('[API] STAR 구매 완료 요청:', maskSensitiveData({
    packageId,
    paymentId,
    txid,
    userId,
    environment
  }));

  return apiCall(url, {
    packageId,
    paymentId,
    txid,
    userId,
    environment
  });
};

// ==================== [NEW] Warm-Up API ====================

/**
 * ☕ Cloud Functions Warm-Up (콜드 스타트 방지)
 * 앱 시작 시 백그라운드로 1회 호출하여 Functions 인스턴스를 깨워둠
 * 결과는 무시하고 에러도 무시 (순수하게 "깨우기" 목적)
 */
/**
 * ☕ Cloud Functions Warm-Up (콜드 스타트 방지)
 * 앱 시작 시 백그라운드로 1회 호출하여 Functions 인스턴스를 깨워둠
 * 비용: 무료 구간 내 충분함 (사용자 10만명 기준 $5 미만)
 */
export const warmupFunctions = async () => {
  const API_URLS = getAPIUrls();

  debugLog('[Warm-Up] ☕ Cloud Functions 전체 예열 시작...');

  const warmupPayload = {
    userId: 'warmup_ping',
    amount: 0,
    source: 'warmup',
    environment: 'sandbox',
    requestId: `warmup_${Date.now()}`,
    piUser: { uid: 'warmup_ping' }, // createSpot 용
    spotData: { type: 'footprint' }, // createSpot 용
    dealId: 'warmup_ping', // purchaseDeal 용
    rewardType: 'warmup' // rewardUser 용
  };

  const headers = { 'Content-Type': 'application/json' };

  try {
    // 🔧 [Parallel] 모든 주요 기능을 동시에 깨움
    await Promise.all([
      // 1. 포인트 시스템 (기존)
      fetch(API_URLS.warmUp, { method: 'POST', headers, body: JSON.stringify(warmupPayload) }), // updatePpPoints

      // 2. 스팟 등록 (포스트/매장)
      fetch(API_URLS.createSpot, { method: 'POST', headers, body: JSON.stringify(warmupPayload) }),

      // 3. 딜 구매
      fetch(API_URLS.purchaseDeal, { method: 'POST', headers, body: JSON.stringify(warmupPayload) }),

      // 4. 티켓 구매
      fetch(API_URLS.purchaseTicket, { method: 'POST', headers, body: JSON.stringify(warmupPayload) })
    ]);

    debugLog('[Warm-Up] ☕ Cloud Functions 전체 예열 완료!');
  } catch (error) {
    debugLog('[Warm-Up] ⚠️ 예열 실패 (무시됨):', error.message);

  }
};

/**
 * ☕ 결제 전용 Warm-Up (결제창 진입 시 콜드 스타트 방지)
 * 콜드 스타트 지연 방지를 위해 결제 관련 함수만 미리 깨움
 */
export const warmupPaymentFunctions = async (environment) => {
  const API_URLS = getAPIUrls();
  const baseUrl = API_URLS.approvePayment.split('/approvePayment')[0];

  debugLog('[Warm-Up] ☕ 결제 전용 API 예열 시작...');

  const warmupPayload = {
    paymentId: 'warmup_ping',
    txid: 'warmup_ping',
    packageId: 'warmup_ping',
    userId: 'warmup_ping',
    environment
  };

  const headers = { 'Content-Type': 'application/json' };

  try {
    await Promise.all([
      fetch(`${baseUrl}/approvePayment`, { method: 'POST', headers, body: JSON.stringify(warmupPayload) }),
      fetch(`${baseUrl}/completePayment`, { method: 'POST', headers, body: JSON.stringify(warmupPayload) }),
      fetch(`${baseUrl}/purchaseStarWithPi`, { method: 'POST', headers, body: JSON.stringify(warmupPayload) }),
      fetch(`${baseUrl}/purchaseSubscriptionWithStar`, { method: 'POST', headers, body: JSON.stringify(warmupPayload) })
    ]);
    debugLog('[Warm-Up] ✅ 결제 API 예열 완료');
    return true;
  } catch (error) {
    debugLog('[Warm-Up] ⚠️ 결제 API 예열 중 실패 (무시됨)', error.message);
    return false;
  }
};

/**
 * 🎯 [신규] 광고 보상 검증 요청
 * @param {string} adId - Pi Ads ID
 * @param {string} rewardType - 'star' | 'hint' | 'heart'
 * @param {string} userId - 사용자 ID
 * @param {string} environment - 환경 정보
 */
export const verifyRewardedAd = async (adId, rewardType, userId, environment) => {
  const API_URLS = getAPIUrls();
  debugLog('[API] 광고 보상 검증 요청:', maskSensitiveData({ adId, rewardType, userId, environment }));
  return apiCall(API_URLS.verifyRewardedAd, { adId, rewardType, userId, environment });
};

/**
 * 🎯 [New] STAR로 구독 구매
 */
export const purchaseSubscriptionWithStar = async (userId, subscriptionType, environment) => {
  const API_URLS = getAPIUrls();
  debugLog('[API] STAR 구독 구매:', maskSensitiveData({ userId, subscriptionType, environment }));
  return apiCall(API_URLS.purchaseSubscriptionWithStar, { userId, subscriptionType, environment });
};

/**
 * 🌍 [World Tour 2026-05-09] 게임 종료 결과 저장
 * - 클라이언트가 게임 종료 시 1회 호출
 * - cityGameWeeklyTC / cityGameTotalTC / cityGamePlayCount / cityGameNation 누적·갱신
 * - 황금 주사위는 인게임 TC 차감으로 전환되어 usedGoldenDice 파라미터 제거됨
 *
 * @param {string} userId
 * @param {number} totalTC  - 게임 종료 시점의 총 자산 (TC + 도시 매각가치 70%)
 * @param {number} earnedTC - 이번 게임에서 순수하게 획득한 TC (어뷰징 한계치 4000)
 * @param {string} nationCode - 'KR'/'US'/.../'WT' (무소속)
 * @param {string} environment - 'production' | 'testnet'
 */
export const saveCityGameResult = async (userId, totalTC, earnedTC, nationCode, environment) => {
  const API_URLS = getAPIUrls();
  debugLog('[API] World Tour 결과 저장:', maskSensitiveData({ userId, totalTC, earnedTC, nationCode, environment }));
  return apiCall(API_URLS.saveCityGameResult, { userId, totalTC, earnedTC, nationCode, environment });
};
